import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Coins, Crown, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const FEATURE_ICONS = { vip_monthly: Crown, creator_subscription_monthly: Lightning };

/**
 * Coin store + subscription store powered by Google Play Billing in the
 * Android wrapper. When running in plain web, falls back to PayPal flow.
 */
export default function CoinShop() {
  const nav = useNavigate();
  const [coins, setCoins] = useState({});
  const [subs, setSubs] = useState({});
  const [busy, setBusy] = useState(null);
  const isAndroid = typeof window !== "undefined" && !!window.GiffwurldancePlayBilling;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/play/products");
        setCoins(data.coins);
        setSubs(data.subscriptions);
      } catch { /* ignore */ }
    })();
  }, []);

  const buy = async (productId, isSubscription) => {
    setBusy(productId);
    try {
      if (isAndroid) {
        // Bridge contract — implement on the native side as window.GiffwurldancePlayBilling
        const result = await window.GiffwurldancePlayBilling.launchBillingFlow({
          productId, isSubscription,
        });
        if (!result?.purchaseToken) throw new Error("No token returned");
        const { data } = await api.post("/play/verify", {
          product_id: productId,
          purchase_token: result.purchaseToken,
          is_subscription: isSubscription,
        });
        if (data.credited) toast.success("Purchase complete — coins added!");
      } else {
        // Web fallback — point user at PayPal coin order flow
        const { data } = await api.post("/wallet/orders", { product_id: productId });
        toast.info("Redirecting to PayPal…");
        const approveUrl = `https://www.sandbox.paypal.com/checkoutnow?token=${data.order_id}`;
        window.location.href = approveUrl;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Purchase failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="coin-shop">
      <div className="px-5 pt-6 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream"><ArrowLeft size={18} /></button>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Store</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">Coins & VIP</h1>
        </div>
      </div>

      <p className="px-5 mt-4 text-cream/60 text-xs tracking-[0.2em] uppercase font-bold">
        {isAndroid ? "Google Play Billing" : "Web · PayPal checkout"}
      </p>

      <section className="px-5 mt-3">
        <p className="text-cream/50 text-[10px] tracking-[0.3em] uppercase font-bold mb-3">Coin packages</p>
        <div className="space-y-2">
          {Object.entries(coins).map(([pid, p]) => (
            <button
              key={pid}
              data-testid={`shop-${pid}`}
              onClick={() => buy(pid, false)}
              disabled={busy === pid}
              className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-papaya transition-colors text-left disabled:opacity-50"
            >
              <div className="w-12 h-12 rounded-2xl bg-papaya/15 text-papaya flex items-center justify-center">
                <Coins size={22} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-cream font-black text-base">{p.label}</p>
                <p className="text-cream/50 text-[11px]">{p.waves} waves</p>
              </div>
              <p className="text-papaya font-black text-lg">${p.usd}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="px-5 mt-8">
        <p className="text-cream/50 text-[10px] tracking-[0.3em] uppercase font-bold mb-3">Subscriptions</p>
        <div className="space-y-2">
          {Object.entries(subs).map(([pid, p]) => {
            const Icon = FEATURE_ICONS[pid] || Crown;
            return (
              <button
                key={pid}
                data-testid={`shop-${pid}`}
                onClick={() => buy(pid, true)}
                disabled={busy === pid}
                className="w-full p-4 rounded-3xl bg-gradient-to-br from-papaya/10 via-white/5 to-jam-gold/5 border border-papaya/30 text-left disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-papaya/20 text-papaya flex items-center justify-center">
                    <Icon size={22} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-cream font-black text-base">{p.label}</p>
                    <p className="text-papaya text-[11px] tracking-[0.2em] uppercase font-bold">${p.usd} / {p.duration_days} days</p>
                  </div>
                </div>
                {p.perks && (
                  <ul className="mt-3 space-y-1">
                    {p.perks.map((perk) => (
                      <li key={perk} className="text-cream/70 text-xs flex items-start gap-2">
                        <span className="text-papaya mt-0.5">•</span> {perk}
                      </li>
                    ))}
                  </ul>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {!isAndroid && (
        <p className="px-5 mt-8 text-cream/40 text-[11px] leading-relaxed">
          On Android, purchases go through Google Play Billing for secure processing.
          On web we route through PayPal. Both grant the same coins / benefits.
        </p>
      )}
    </div>
  );
}
